/** @format */

import database from '../firebase/config';
import { observable, action } from 'mobx';
import { getTimestamp } from '../helpers/getTimestamp';
import ResourceStore from './classes/ResourceStore';
import { hasValue } from '../helpers/validateForm';

class CarsStore extends ResourceStore {
  @observable ref = `dropdown/cars`;
  @observable defaultItem = {
    makerUid: '',
    modelUid: '',
    versionUid: '',
    maker: '',
    model: '',
    version: '',
  };
  @observable edit = {
    currentItem: {
      makerUid: null,
      modelUid: null,
      versionUid: null,
      maker: '',
      model: '',
      version: '',
    },
    modal: false,
    newModal: false,
    editModal: false,
    newModelEnabled: true,
    newVersionEnabled: false,
    errors: [],
  };
  @observable carsMakers = [];
  @observable carsModels = [];
  @observable carsVersions = [];
  @observable tableKeys = [{ label: 'Costruttore', prop: 'name' }];
  @observable search = {
    value: '',
    attributes: ['name'],
  };

  /**
   * Fetch dei costruttori di macchine dal LocalStorage
   */
  @action fetchCarMakers = () => {
    const localCars = JSON.parse(localStorage.getItem('cars'));
    const carMakers = localCars.carMakers;
    const carMakersKeys = Object.keys(carMakers);
    this.carsMakers = [];
    this.items = [];

    carMakersKeys.forEach(carMakerKey => {
      this.carsMakers.push({
        text: carMakers[carMakerKey],
        value: carMakers[carMakerKey],
        id: carMakerKey,
      });

      this.items.push({
        uid: carMakerKey,
        name: carMakers[carMakerKey],
      });
    });
  };

  /**
   * Fetch dei modelli di macchine dal LocalStorage
   */
  @action fetchCarModels = maker => {
    const localCars = JSON.parse(localStorage.getItem('cars'));
    const carMakers = maker ? [maker] : [];
    this.carsModels = [];

    carMakers.forEach(carMaker => {
      const carModels = localCars.carModels[carMaker];
      if (!carModels) {
        return;
      }
      const carModelsKeys = Object.keys(carModels);
      carModelsKeys.forEach(carModelKey => {
        if (carModels[carModelKey] !== null) {
          this.carsModels.push({
            text: carModels[carModelKey],
            value: carModels[carModelKey],
            id: carModelKey,
            maker: carMaker,
          });
        }
      });
    });
  };

  /**
   * Fetch delle versioni di macchine dal LocalStorage
   */
  @action fetchCarVersions = model => {
    const localCars = JSON.parse(localStorage.getItem('cars'));
    const carModels = model ? [model] : [];
    this.carsVersions = [];
    carModels.forEach(carModel => {
      const carVersions = localCars.carVersions[carModel];

      if (!carVersions) {
        return;
      }
      const carVersionsKeys = Object.keys(carVersions);
      carVersionsKeys.forEach(carVersionsKey => {
        if (carVersions[carVersionsKey] !== null) {
          this.carsVersions.push({
            text: carVersions[carVersionsKey],
            value: carVersions[carVersionsKey],
            id: carVersionsKey,
            model: carModel,
          });
        }
      });
    });
  };

  /**
   * Fetch delle macchine dal LocalStorage (se presenti), da Firebase altrimenti
   */
  @action fetchAllCars = () => {
    const carDbVersionRef = database.ref('/dropdowns/cars/carDbVersion');
    let localCars = localStorage.getItem('cars')
      ? JSON.parse(localStorage.getItem('cars'))
      : {};

    carDbVersionRef.on('value', dbVersionSnap => {
      const dbVersion = dbVersionSnap.val();

      if (localCars.carDbVersion !== dbVersion) {
        let cars = { carDbVersion: dbVersion };
        const carMakersRef = database.ref('/dropdowns/cars/carMakers/');
        carMakersRef.once('value', makerSnap => {
          const carModelsRef = database.ref('/dropdowns/cars/carModels/');
          cars.carMakers = makerSnap.val();
          carModelsRef.once('value', modelSnap => {
            const carVersionsRef = database.ref('/dropdowns/cars/carFullName/');
            cars.carModels = modelSnap.val();
            carVersionsRef.once('value', versionSnap => {
              cars.carVersions = versionSnap.val();
              localStorage.setItem('cars', JSON.stringify(cars));
              this.fetchCarMakers();
            });
          });
        });
      } else {
        this.fetchCarMakers();
      }
    });
  };

  @action getMakers = () => {
    return this.carsMakers;
  };

  @action getMaker = uid => {
    return this.carsMakers.find(maker => maker.id === uid);
  };

  @action getModel = uid => {
    return this.carsModels.find(model => model.id === uid);
  };

  @action getVersion = uid => {
    return this.carsVersions.find(version => version.id === uid);
  };

  @action getMakerModels = uid => {
    return this.carsModels.filter(model => model.maker === uid);
  };

  @action getModelVersions = uid => {
    return this.carsVersions.filter(version => version.model === uid);
  };

  @action handleInputChange = (value, name) => {
    this.edit.currentItem[name] = value;

    if (name === 'makerUid') {
      this.fetchCarModels(value);
      if (value === null) {
        this.edit.currentItem.modelUid = null;
        this.edit.currentItem.versionUid = null;
      } else {
        this.edit.currentItem.uid = value;
      }
    } else if (name === 'modelUid' && value) {
      this.fetchCarVersions(value);
      this.edit.currentItem.model = this.getModel(value).text;
      this.edit.currentItem.versionUid = null;
    } else if (name === 'versionUid' && value) {
      this.edit.currentItem.version = this.getVersion(value).text;
    }
  };

  /**
   * Setta la risorsa con id uguale a uid come risorsa in modifica
   * @param {String} uid
   */
  @action loadCurrentItem = uid => {
    const item = this.getItems.find(item => item.uid === uid);
    this.edit.currentItem.uid = item.uid;
    this.edit.currentItem.makerUid = item.uid;
    this.edit.currentItem.maker = this.getMaker(item.uid).text;
    this.fetchCarModels(item.uid);
  };

  /**
   * Per questo store ci sono 2 funzioni per toogglare il modal
   * Questa abilita/disabilita il modal in modalità 'new' e quindi
   * viene predisposto ad aggiungere nuovi modelli/versioni
   */
  @action toggleNewModal = () => {
    this.edit.newModal = !this.edit.newModal;
    this.edit.modal = !this.edit.modal;
    this.edit.newModelEnabled = true;
    this.edit.newVersionEnabled = false;
    this.resetCurrentItem();
  };

  /**
   * Per questo store ci sono 2 funzioni per toogglare il modal
   * Questa abilita/disabilita il modal in modalità 'new' e quindi
   * viene predisposto modificare costruttori/modelli/versioni
   */
  @action toggleEditModal = () => {
    this.edit.editModal = !this.edit.editModal;
    this.edit.modal = !this.edit.modal;
    this.edit.newModelEnabled = false;
    this.edit.newVersionEnabled = false;
    this.resetCurrentItem();
  };

  /**
   * Toggle dellì'input per aggiungere/modificare una nuovo modello
   */
  @action toggleModel() {
    this.edit.newModelEnabled = !this.edit.newModelEnabled;
    this.edit.newVersionEnabled =
      this.edit.newModelEnabled === false ? false : this.edit.newVersionEnabled;
  }

  /**
   * Toggle dellì'input per aggiungere/modificare una nuova versione
   */
  @action toggleVersion() {
    this.edit.newVersionEnabled = !this.edit.newVersionEnabled;
  }

  /**
   * Setta l'item nel localStorage coerentemente con le modifiche fatte
   * @param {String} collection Il nome del paramentro del localStorage.cars da modificare
   * @param {Object} item
   */
  @action setItem = (collection, item) => {
    let localCars = JSON.parse(localStorage.getItem('cars'));

    if (item.parentId !== undefined) {
      if (!localCars[collection][item.parentId]) {
        localCars[collection][item.parentId] = {};
      }
      localCars[collection][item.parentId][item.uid] = item.value;
    } else {
      localCars[collection][item.uid] = item.value;
    }

    localStorage.setItem('cars', JSON.stringify(localCars));
  };

  /**
   * Rimuove l'item nel localStorage coerentemente con le modifiche fatte
   * @param {String} collection Il nome del paramentro del localStorage.cars da modificare
   * @param {Object} item
   */
  @action removeItem = (collection, item) => {
    let localCars = JSON.parse(localStorage.getItem('cars'));

    if (hasValue(item.parentId)) {
      delete localCars[collection][item.parentId][item.uid];
    } else {
      delete localCars[collection][item.uid];
    }

    localStorage.setItem('cars', JSON.stringify(localCars));
  };

  /**
   * Submit della versione, modello o maker modificato/aggiunto
   */
  @action submit = async () => {
    const item = this.edit.currentItem;

    if (item.maker) {
      const maker = await this.submitItem('/dropdowns/cars/carMakers/', {
        name: item.maker,
        uid: item.makerUid,
      });

      this.setItem('carMakers', {
        uid: maker.uid,
        value: item.maker,
      });
      this.fetchCarMakers();

      item.makerUid = maker.uid;
    }

    if (item.model) {
      const model = await this.submitItem(
        `/dropdowns/cars/carModels/${item.makerUid}`,
        {
          name: item.model,
          uid: item.modelUid,
        },
      );

      this.setItem('carModels', {
        uid: model.uid,
        parentId: item.makerUid,
        value: item.model,
      });
      this.fetchCarModels(item.makerUid);

      item.modelUid = model.uid;
    }

    if (item.version) {
      const version = await this.submitItem(
        `/dropdowns/cars/carFullName/${item.modelUid}`,
        {
          name: item.version,
          uid: item.versionUid,
        },
      );

      this.setItem('carVersions', {
        uid: version.uid,
        parentId: item.modelUid,
        value: item.version,
      });
      this.fetchCarVersions(item.modelUid);

      item.versionUid = version.uid;
    }

    this.toggleEditModal();
    this.resetCurrentItem();
  };

  /**
   * L'effettiva chiamata di modifica/aggiunta
   * @param {String} baseRef La reference
   * @param {Object} currentItem L'item da aggiungere/modificare
   * @return {Promise}
   */
  @action submitItem = (baseRef, currentItem) => {
    currentItem.uid = currentItem.uid ? currentItem.uid : '';
    currentItem.timestamp = getTimestamp();
    const method = currentItem.uid !== '' ? 'set' : 'push';
    const ref = database.ref(`${baseRef}/${currentItem.uid}`);

    return new Promise((resolve, reject) => {
      ref[method](currentItem.name)
        .then(item => {
          if (item && method === 'push') {
            currentItem.uid = item.key;
          }

          return resolve(currentItem);
        })
        .catch(() => {
          this.edit.errors = [
            'Non è stato possibile eseguire questa operazione',
          ];
          reject(this.edit.errors);
        });
    });
  };

  @action deleteItem = uid => {
    const item = this.edit.currentItem;

    if (hasValue(item.versionUid) && hasValue(item.modelUid)) {
      return database
        .ref(`/dropdowns/cars/carFullName/${item.modelUid}/${item.versionUid}`)
        .remove(response => {
          this.removeItem('carVersions', {
            parentId: item.modelUid,
            uid: item.versionUid,
          });

          this.toggleEditModal();
          this.resetCurrentItem();
        });
    }

    if (hasValue(item.modelUid) && hasValue(item.makerUid)) {
      return database
        .ref(`/dropdowns/cars/carModels/${item.makerUid}/${item.modelUid}`)
        .remove(response => {
          this.removeItem('carModels', {
            parentId: item.makerUid,
            uid: item.modelUid,
          });

          this.toggleEditModal();
          this.resetCurrentItem();
        });
    }

    if (hasValue(item.makerUid)) {
      return database
        .ref(`/dropdowns/cars/carMakers/${item.makerUid}`)
        .remove(response => {
          delete this.items[item.makerUid];
          this.removeItem('carModels', {
            uid: item.makerUid,
          });

          this.toggleEditModal();
          this.resetCurrentItem();
        });
    }
  };
}

export default new CarsStore();
